import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation, useMatch } from "react-router-dom";
import { Button, Collapse, Drawer, Grid, Group, Modal, Stack, Text, Title, useMantineTheme } from "@mantine/core";
import { FileWithPath } from "@mantine/dropzone";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import { IconPlus } from "@tabler/icons-react";
import moment from "moment";

import { useCreateWorkOrderMutation, useDeleteWorkOrderMutation, useGetWorkOrderByIdQuery, useGetWorkOrdersQuery, useUpdateWorkOrderMutation } from "../../api/workorder.api";
import { useGetWorkOrderTemplateByIdQuery } from "../../api/workordertemplate.api";
import { ListWorkOrderDto, MutableWorkOrderDto, WorkOrderDto, WorkOrderStatus } from "../../models/workorder.models";
import { WorkOrderList } from "../../Components/WorkOrders/WorkOrderList";
import { WorkOrderDetailView } from "../../Components/WorkOrders/WorkOrderDetailView";
import { WorkOrderEditView } from "../../Components/WorkOrders/WorkOrderEditView";
import { AzureADRoles } from "../../Auth/auth-roles";
import { useAccess } from "../../Auth/auth-functions";
import { RoleTemplate } from "../../Components/RoleTemplate";
import { useSearch } from "../../functions/SearchBox.hook";
import { useWorkOrderFilters } from "../../functions/workorder-filter.hooks";

const WorkOrdersPage = () => {
	const navigate = useNavigate();
	const history = useLocation();
	const theme = useMantineTheme();
	const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`);
	const params = useParams<{ workOrderId?: string, workOrderTemplateId?: string }>();
	const isEditing = useMatch("/workorders/:workOrderId/edit") !== null;
	const isCopying = useMatch("/workorders/:workOrderId/copy") !== null;
	const workOrderIdFromParams = params.workOrderId || null;
	const { SearchBox, visible: searchVisible } = useSearch();
	const { FilterBar } = useWorkOrderFilters();
	const isFullUser = useAccess(AzureADRoles.FullUser);
	
	const isNewFromTemplate = useMatch("/workorders/:workOrderId/template/:workOrderTemplateId") !== null;
	const { data: workOrderTemplate } = useGetWorkOrderTemplateByIdQuery(isNewFromTemplate ? params.workOrderTemplateId || null : null);
	
	const { data: workOrders, isPending: isLoadingWorkOrders } = useGetWorkOrdersQuery();	
	const [selectedWorkOrder, setSelectedWorkOrder] = useState<ListWorkOrderDto | null>(null);
	const { data: workOrder, isLoading } = useGetWorkOrderByIdQuery(selectedWorkOrder?.WorkOrderId || workOrderIdFromParams || null);
	const isRequest = (workOrder != null && workOrder.Status == WorkOrderStatus.Requested) || workOrderIdFromParams === "new-request" || false;
	const isNew = workOrderIdFromParams === "new" || workOrderIdFromParams === "new-request" || isCopying || isNewFromTemplate;
	const [detailViewDrawerOpened, { open: openDetailViewDrawer, close: closeDetailViewDrawer }] = useDisclosure(false);

	// const [isediting, setIsEditing] = useState(false);
	const [isChanged, setIsChanged] = useState(false);
	const [requestedWorkOrderId, setRequestedWorkOrderId] = useState<string | null>(null);

	const { mutate: createWorkOrder, isPending: isCreating } = useCreateWorkOrderMutation();
	const { mutate: updateWorkOrder, isPending: isUpdating } = useUpdateWorkOrderMutation();
	const { mutate: deleteWorkOrder } = useDeleteWorkOrderMutation();

	const [discardChangesModalOpened, { open: openDiscardChangesModal, close: closeDiscardChangesModal }] = useDisclosure(false);
	const [deleteModalOpen, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);

	const isSaving = isCreating || isUpdating;

	const templateWorkOrder: WorkOrderDto | null = isNewFromTemplate && workOrderTemplate ? {
		...workOrderTemplate,
		WorkOrderId: workOrderIdFromParams ?? "new",
		WorkOrderNumber: "",
		StartedDate: workOrderTemplate.DueDays ? moment().toDate() : undefined,
		DueDate: workOrderTemplate.DueDays ? moment().add(workOrderTemplate.DueDays, 'days').toDate() : undefined,
		Created: moment().toDate(),
		Updated: null,
		Deleted: null,
		Files: [],
		Status: isRequest ? WorkOrderStatus.Requested : WorkOrderStatus.Open,
		Events: []
	} : null;

	const copiedWorkOrder: WorkOrderDto | null = isCopying && workOrder ? {
		...workOrder,
		WorkOrderNumber: "",
		WorkOrderId: "",
		Title: workOrder.Title ?? "",
		Description: workOrder.Description ?? "",
		Created: moment().toDate(),
		Updated: null,
		Deleted: null,
		Files: []
	} : null;
	
	useEffect(() => {
		if (isFullUser && isRequest && isNewFromTemplate) {
			navigate(`/workorders/new/template/${workOrderTemplate?.WorkOrderTemplateId}`, { replace: true });
			return;
		}

		workOrders?.sort((a, b) => new Date(b.Created).getTime() - new Date(a.Created).getTime());
		if (workOrderIdFromParams) {
			const workOrder = workOrders?.find((wo: ListWorkOrderDto) => wo.WorkOrderId === workOrderIdFromParams);
			if (workOrder) {
				setSelectedWorkOrder?.(workOrder);
				if (isMobile) {
					openDetailViewDrawer();
				}
			} else {
				if (isMobile && isNew) {
					openDetailViewDrawer();
				}
				setSelectedWorkOrder?.(null);
			}
		} else if (workOrders && workOrders.length > 0) {
			const requestedWorkOrders = workOrders?.filter(wo => wo.Status === WorkOrderStatus.Requested && wo.Deleted == null) || [];
			const todoWorkOrders = workOrders?.filter(wo => wo.Status != WorkOrderStatus.Completed && wo.Status != WorkOrderStatus.Archived && wo.Status != WorkOrderStatus.Requested && wo.Deleted == null) || [];
			const doneWorkOrders = workOrders?.filter(wo => wo.Status === WorkOrderStatus.Completed && wo.Deleted == null) || [];
			const archivedWorkOrders = workOrders?.filter(wo => wo.Status === WorkOrderStatus.Archived && wo.Deleted == null) || [];
			const deletedWorkOrders = workOrders?.filter(wo => wo.Deleted != null) || [];

			if (requestedWorkOrders.length > 0) {
				setSelectedWorkOrder?.(requestedWorkOrders[0]);
			} else if (todoWorkOrders.length > 0) {
				setSelectedWorkOrder?.(todoWorkOrders[0]);
			} else if (doneWorkOrders.length > 0) {
				setSelectedWorkOrder?.(doneWorkOrders[0]);
			} else if (archivedWorkOrders.length > 0) {
				setSelectedWorkOrder?.(archivedWorkOrders[0]);
			} else if (deletedWorkOrders.length > 0) {
				setSelectedWorkOrder?.(deletedWorkOrders[0]);
			}
			// setSelectedWorkOrder?.(workOrders[0]);
		}
	}, [workOrderIdFromParams, workOrders, isMobile]);

	useEffect(() => {
		if (!workOrderIdFromParams && isMobile) {
			closeDetailViewDrawer();
		}
	}, [history.pathname]);

	const handleNewWorkOrderRequest = () => {
		// setIsEditing(true);
		setIsChanged(false);
		setSelectedWorkOrder(null);
		navigate(`/workorders/new-request`);

		if (isMobile) {
			openDetailViewDrawer();
		}
	};

	const handleNewWorkOrder = () => {
		// setIsEditing(true);
		setIsChanged(false);
		setSelectedWorkOrder(null);
		navigate(`/workorders/new`);

		if (isMobile) {
			openDetailViewDrawer();
		}
	};

	const handleEdit = () => {
		navigate(`/workorders/${workOrder?.WorkOrderId}/edit`);	
	}

	const handleWorkOrderSelect = (workOrder: ListWorkOrderDto | null) => {
		if (!workOrder)
			return;

		setRequestedWorkOrderId(workOrder.WorkOrderId);

		if (isChanged && !discardChangesModalOpened) {
			openDiscardChangesModal();
			return;
		}

		setSelectedWorkOrder(workOrder);
		navigate(`/workorders/${workOrder.WorkOrderId}`);
		// setIsEditing(false);
		setIsChanged(false);

		if (isMobile) {
			openDetailViewDrawer();
		}
	};

	const handleSave = (workOrder: MutableWorkOrderDto, files: FileWithPath[]) => {
		if (isNewFromTemplate) {
			workOrder.WorkOrderTemplateId = workOrderTemplate?.WorkOrderTemplateId;
		}

		if (isNew) {
			createWorkOrder({ workOrder, files }, {
				onSuccess: (response) => {
					setIsChanged(false);
					// setIsEditing(false);
					navigate(`/workorders/${response.data.WorkOrderId}`);
				}
			});
		} else {
			updateWorkOrder({ workOrder, files }, {
				onSuccess: () => {
					// setSelectedWorkOrder(workOrder);
					setIsChanged(false);
					// setIsEditing(false);
					// history.replace(`/workorders/${workOrder.WorkOrderId}`);
					navigate(-1);
				}
			});
		}
	};

	const handleCopy = () => {
		if (!workOrder) return;

		navigate(`/workorders/${workOrder?.WorkOrderId}/copy`);
	};

	const handleDelete = () => {
		if (!workOrder) return;

		closeDeleteModal();
		deleteWorkOrder(workOrder, {
			onSuccess: () => {
				setSelectedWorkOrder(null);
				navigate(`/workorders`, { replace: true });
			}
		});
	}

	const handleClose = () => {
		if (isChanged && !discardChangesModalOpened) {
			openDiscardChangesModal();
		} else {
			closeDiscardChangesModal();
			// setSelectedWorkOrder(null);
			// setIsEditing(false);
			// history.replace(`/workorders/${workOrder?.WorkOrderId}`);
			setIsChanged(false);

			// if (selectedWorkOrder == null)	
			// if (!isMobile)
				navigate(-1);
			// history.push(`/workorders`);
			if (isMobile && isNew) {
				closeDetailViewDrawer();
			}
		}
	}

	const handleDiscardChanges = () => {
		closeDiscardChangesModal();
		// setIsEditing(false);
		setIsChanged(false);

		// if (selectedWorkOrder == null){
			// history.push(`/workorders`);
			// history.goBack();
			// return;
		// }
		if (isMobile && (isNew || isRequest)) {
			closeDetailViewDrawer();
		}

		if (requestedWorkOrderId && requestedWorkOrderId !== workOrder?.WorkOrderId) {
			navigate(`/workorders/${requestedWorkOrderId}`);
			// history.goBack();
			setRequestedWorkOrderId(null);
		} else {
			navigate(-1);
		}
	}

	const handleDetailViewDrawerClose = () => {
		navigate(-1);
	}

	return <>
		<DiscardChangesModal open={discardChangesModalOpened} onDiscard={handleDiscardChanges} onClose={closeDiscardChangesModal} />
		<DeleteWorkOrderModal workOrder={workOrder} open={deleteModalOpen} onDelete={handleDelete} onClose={closeDeleteModal} />
		<Drawer opened={detailViewDrawerOpened} onClose={handleDetailViewDrawerClose} withCloseButton={false} padding={0} size="xl" position="right">
			{!isEditing && !isNew &&
				<WorkOrderDetailView workOrder={workOrder} onEdit={handleEdit} onCopy={handleCopy} onDelete={openDeleteModal} onClose={handleDetailViewDrawerClose} isLoading={isLoading} />
			}
			{isEditing && workOrder &&
				<WorkOrderEditView workOrder={workOrder} isCopy={false} isRequest={isRequest} onSave={handleSave} isSaving={isSaving} onChanged={setIsChanged} onClose={handleClose} />
			}
			{isNew && !isNewFromTemplate &&
				<WorkOrderEditView workOrder={isCopying && copiedWorkOrder ? copiedWorkOrder : undefined} isCopy={isCopying} isRequest={isRequest} onSave={handleSave} isSaving={isSaving} onChanged={setIsChanged} onClose={handleClose} />
			}
			{isNew && isNewFromTemplate && templateWorkOrder &&
				<WorkOrderEditView workOrder={templateWorkOrder} isCopy={true} isRequest={isRequest} onSave={handleSave} isSaving={isSaving} onChanged={setIsChanged} onClose={handleClose} />
			}
		</Drawer>
					
		<Collapse in={searchVisible} mb="md" hiddenFrom="sm">
			{SearchBox}	
		</Collapse>
		
		<Group justify="space-between">
			<Title order={2}>Work Orders</Title>
			<Group gap="xs">
				<Button onClick={handleNewWorkOrderRequest} radius="xl" leftSection={<IconPlus />}>New Request</Button>
				<RoleTemplate role={AzureADRoles.AdminOrFullUser}>
					<Button onClick={handleNewWorkOrder} radius="xl" leftSection={<IconPlus />}>New Work Order</Button>
				</RoleTemplate>
			</Group>
		</Group>

		{FilterBar}

		<Grid mt="lg" gutter="md">
			<Grid.Col span={{ base: 12, xs: ((!workOrders || workOrders.length === 0) && !isNew && !isEditing) ? 12 : 5 }}>
				<WorkOrderList workOrders={workOrders} isLoading={isLoadingWorkOrders} selectedWorkOrder={selectedWorkOrder} setSelectedWorkOrder={handleWorkOrderSelect} />
			</Grid.Col>
			
			<Grid.Col span={{ base: 12, xs: 7, md: 7, lg: 7 }} visibleFrom="xs">
				{!isEditing && !isNew && selectedWorkOrder &&
					<WorkOrderDetailView workOrder={workOrder} onEdit={handleEdit} onCopy={handleCopy} onDelete={openDeleteModal} isLoading={isLoading} />
				}
				{!isEditing && !isNew && !selectedWorkOrder &&
					<WorkOrderDetailView workOrder={undefined} onEdit={handleEdit} onCopy={handleCopy} onDelete={openDeleteModal} isLoading={isLoading} />
				}
				{isEditing && workOrder &&
					<WorkOrderEditView workOrder={workOrder} isCopy={false} isRequest={isRequest} onSave={handleSave} isSaving={isSaving} onChanged={setIsChanged} onClose={handleClose} />
				}
				{isNew && !isNewFromTemplate &&
					<WorkOrderEditView workOrder={isCopying && copiedWorkOrder ? copiedWorkOrder : undefined} isCopy={isCopying} isRequest={isRequest} onSave={handleSave} isSaving={isSaving} onChanged={setIsChanged} onClose={handleClose} />
				}
				{isNew && isNewFromTemplate && templateWorkOrder &&
					<WorkOrderEditView workOrder={templateWorkOrder} isCopy={true} isRequest={isRequest} onSave={handleSave} isSaving={isSaving} onChanged={setIsChanged} onClose={handleClose} />
				}
			</Grid.Col>
			
		</Grid>

	</>
}

export default WorkOrdersPage;

const DiscardChangesModal = ({ open, onDiscard, onClose }: { open: boolean, onClose: () => void, onDiscard: () => void }) => (
	<Modal opened={open} onClose={onClose} title={<Text fz="xl" fw="bold">Discard unsaved changes?</Text>} radius="lg" zIndex={1000}>
		<Stack>
			{/* <Title order={4}>Discard unsaved changes?</Title> */}
			<Text>If you leave now, you’ll lose unsaved changes to this work order.</Text>
			<Group justify="right">
				<Button variant="outline" onClick={onClose} radius="lg">Cancel</Button>
				<Button variant="filled" onClick={onDiscard} radius="lg">Discard Changes</Button>
			</Group>
		</Stack>
	</Modal>
);

const DeleteWorkOrderModal = ({ workOrder, open, onDelete, onClose }: { workOrder?: WorkOrderDto, open: boolean, onDelete: () => void, onClose: () => void }) => {
	const isRequest = workOrder?.Status == WorkOrderStatus.Requested;
	return (
		<Modal opened={open} onClose={onClose} title={<Title fz="xl" fw="bold">Delete {isRequest ? "Request" : "Work Order"}</Title>} radius="lg" zIndex={1000}>
			<Stack>
				<Text>Are you sure you want to delete the {isRequest ? "request" : "work order"}:</Text>
				<Text fw="bold">#{workOrder?.WorkOrderNumber} - {workOrder?.Title}</Text>
				<Group justify="right">
					<Button variant="outline" onClick={onClose} radius="lg">Cancel</Button>
					<Button color="red" onClick={onDelete} radius="lg">Delete</Button>
				</Group>
			</Stack>
		</Modal>
	);
}